import { createFileRoute, Link } from '@tanstack/react-router'
import { DocPage } from '../components/docs/DocPage'
import { Section, P, DefList, C, Pre, H3 } from '../components/docs/prose'

export const Route = createFileRoute('/docs/automating')({ component: Automating })

const SECTIONS = [
  { id: 'connections', title: 'Bank connections' },
  { id: 'signs', title: 'The sign convention' },
  { id: 'detected-bills', title: 'Detected bills' },
  { id: 'reconcile', title: 'Running reconciliation' },
  { id: 'recurring', title: 'Recurring transactions' },
  { id: 'activity-log', title: 'Activity log' },
  { id: 'limits', title: 'What is still manual' },
]

function Automating() {
  return (
    <DocPage
      path="/docs/automating"
      title="Automating it"
      lede={
        <>
          Everything in the previous pages can be done by hand, correctly, and
          nobody sustains that past about six weeks. This page is how you stop.
        </>
      }
      sections={SECTIONS}
    >
      <Section id="connections" title="Bank connections">
        <P>
          Accounts are linked through Plaid, configured for Canadian
          institutions. Linking exchanges a short-lived public token for an
          access token, which is encrypted with AES-256-GCM before it touches
          the database. The plaintext token exists only in memory during the
          exchange.
        </P>
        <H3>Read-only by construction</H3>
        <P>
          FolioWorth requests the transactions product only. The Plaid client
          in this codebase implements five calls: create a link token, exchange
          a public token, list accounts, sync transactions, and look up an
          institution. There is no payment or transfer call to misuse, because
          none is written.
        </P>
        <H3>How syncing works</H3>
        <P>
          Sync is cursor-based. Each connection stores where it last read to,
          and each run pages forward from there until the institution says
          there is no more. The cursor is saved after each page commits, so an
          interrupted sync resumes rather than restarts.
        </P>
        <P>
          Re-running a sync cannot duplicate anything. Every imported entry
          carries an <C>external_id</C> of <C>plaid:&lt;transaction id&gt;</C>,
          unique per household, and a second attempt to write it is a no-op.
        </P>
        <P>
          Pending transactions are skipped until they post, and zero-amount
          transactions are ignored. Transfers, credit-card payments and loan
          payments are routed to a clearing account rather than being booked as
          expenses, so paying your Visa does not show up as spending. Bank fees
          are deliberately not treated as transfers — a fee is a real expense.
        </P>
        <P>
          One thing to know: <strong>sync is something you trigger</strong>.
          There is no webhook listener and no background scheduler in this
          build, so transactions arrive when you ask for them.
        </P>
      </Section>

      <Section id="signs" title="The sign convention">
        <P>
          Plaid reports a purchase as a <em>positive</em> number: positive
          means money left the account. FolioWorth&rsquo;s ledger uses the
          opposite convention, where positive means value arrived. The import
          negates every amount at the boundary:
        </P>
        <Pre>{`// Flip Plaid's convention: their positive (outflow) becomes our negative.
signed := t.Amount.Neg()`}</Pre>
        <P>
          This is worth knowing if you ever compare a raw Plaid response to
          what you see in FolioWorth, or write against the API. Inside the
          ledger, one convention holds everywhere.
        </P>
      </Section>

      <Section id="detected-bills" title="Detected bills">
        <P>
          Rather than making you enumerate your payees, FolioWorth reads twelve
          months of outflows and proposes the ones that look recurring. The
          test is deliberately conservative, because a wrong suggestion costs
          more attention than a missing one.
        </P>
        <DefList
          items={[
            ['Twelve-month window', 'Outflows only, from the last twelve months of transactions.'],
            ['At least three distinct months', 'Two months is a coincidence. Three is a pattern.'],
            ['Amount stable within 25%', 'The standard deviation of the amounts must be no more than a quarter of their mean. A grocery run varies far more than that; a phone bill does not.'],
            ['Not already a payee', 'Candidates are matched against your existing payees by exact name, substring, and significant words, so nothing you already track is suggested again.'],
            ['Transfers excluded', 'Descriptions that look like transfers, e-transfers, deposits, withdrawals, contributions or top-ups are filtered out. Moving your own money is not a bill.'],
          ]}
        />
        <P>
          Each suggestion arrives with a median amount, the number of months
          seen, the most common day of the month, and a guessed category from
          the description. At most twelve are returned, ordered by how many
          months they appeared in. Nothing is created until you accept it.
        </P>
      </Section>

      <Section id="reconcile" title="Running reconciliation">
        <P>
          Reconciliation is a single action across the household: it walks
          every bill you marked paid that has no transaction linked, and
          matches what it can under{' '}
          <Link to="/docs/reconciliation" style={{ color: 'var(--accent-text)' }}>
            the rules on the reconciliation page
          </Link>. It reports how many it matched.
        </P>
        <P>
          The habit worth building is two clicks in sequence: sync the
          connection, then run reconciliation. Everything that can be resolved
          resolves in one pass, and what is left in <C>unposted</C> is a real
          list of things that need you.
        </P>
      </Section>

      <Section id="recurring" title="Recurring transactions">
        <P>
          Separate from bills, FolioWorth can detect repeating transactions in
          general — subscriptions, transfers you make every payday, standing
          debits. Detection groups the last twelve months by description,
          rounded amount, account and category, and keeps groups seen at least
          three times.
        </P>
        <P>
          The frequency is inferred from the average gap between occurrences:
        </P>
        <Pre>{`5–9 days      weekly
12–17 days    biweekly
25–35 days    monthly
80–100 days   quarterly
350–380 days  yearly`}</Pre>
        <P>
          Anything that does not land in one of those bands is discarded rather
          than guessed at. Candidates are returned for you to accept; detection
          writes nothing on its own.
        </P>
        <P>
          Once saved, a recurring transaction is a forecast, not a posting
          rule. The upcoming view is a thirty-day lookahead of what is due.
          Nothing is written to the ledger on your behalf — a recurring entry
          you did not verify is a wrong balance you will not notice.
        </P>
      </Section>

      <Section id="limits" title="What is still manual">
        <P>
          Stated plainly, because the alternative is you discovering it later:
        </P>
        <DefList
          items={[
            ['Syncing', 'You trigger it. No webhooks, no scheduler.'],
            ['Reconciliation', 'You trigger it. It does not run after a sync by itself.'],
            ['Recurring instances', 'Never auto-posted. Forecast only.'],
            ['Accepting suggestions', 'Detected bills and detected recurring transactions are proposals. You confirm each one.'],
          ]}
        />
        <P>
          The pattern in all four is the same: FolioWorth will do the finding,
          and leaves the asserting to you. That is a deliberate stance for a
          ledger whose main promise is that its numbers are exact.
        </P>
      </Section>
      <Section id="activity-log" title="Activity log">
        <P>
          Everything the system does on your behalf is recorded: syncs,
          reconnections, imports, account edits, valuations, member changes.
          Searchable, filterable and sortable, under <strong>Activity</strong>.
        </P>
        <P>
          The log is <strong>append-only</strong>, enforced by database
          permissions rather than convention &mdash; the application can insert
          and read, and has no grant to update or delete. A record of what
          happened that can be quietly revised is not a record.
        </P>
        <P>
          It is the first place to look when a number moves and you do not know
          why: a sync that pulled a restated balance, an import that added
          transactions, or a valuation someone in the household updated.
        </P>
      </Section>

    </DocPage>
  )
}
